import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
}

export function ForgotPasswordModal({ isOpen, onClose, initialEmail = '' }: ForgotPasswordModalProps) {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (error: any) {
      // Error handling is done in AuthContext with toast notifications
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail(initialEmail);
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  const handleBackToLogin = () => {
    handleClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
            style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="relative bg-gradient-to-r from-jamaica-green to-jamaica-green/90 px-6 py-8 text-white">
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                    className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    {success ? (
                      <CheckCircle className="w-8 h-8" />
                    ) : (
                      <Mail className="w-8 h-8" />
                    )}
                  </motion.div>
                  
                  <h2 className="text-2xl font-bold mb-2">
                    {success ? 'Check Your Email' : 'Forgot Password?'}
                  </h2>
                  
                  <p className="text-white/90 text-sm">
                    {success 
                      ? "We've sent a password reset link to your email address."
                      : "No worries! Enter your email and we'll send you a reset link."
                    }
                  </p>
                </div>
              </div>

              <div className="p-6">
                {success ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center space-y-6"
                  >
                    <div className="space-y-2">
                      <p className="text-gray-600">
                        Check your inbox and click the link to reset your password.
                      </p>
                      <p className="text-sm text-gray-500">
                        Didn't receive the email? Check your spam folder or try again.
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <Button
                        onClick={() => {
                          setSuccess(false);
                          setEmail(initialEmail);
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        Send Another Email
                      </Button>
                      
                      <Button
                        onClick={handleBackToLogin}
                        className="w-full bg-jamaica-green hover:bg-jamaica-green/90"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Login
                      </Button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.form
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    onSubmit={handleSubmit}
                    className="space-y-6"
                  >
                    <div>
                      <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          id="reset-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email address"
                          className="pl-10 h-12"
                          required
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button
                          type="submit"
                          disabled={loading || !email}
                          className="w-full h-12 text-base font-medium bg-gradient-to-r from-jamaica-green to-jamaica-green/90 hover:from-jamaica-green/90 hover:to-jamaica-green shadow-lg"
                        >
                          {loading ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Sending...</span>
                            </div>
                          ) : (
                            'Send Reset Link'
                          )}
                        </Button>
                      </motion.div>
                      
                      <Button
                        type="button"
                        onClick={handleClose}
                        variant="ghost"
                        className="w-full text-gray-600 hover:text-gray-800"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Login
                      </Button>
                    </div>
                  </motion.form>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
} 